import React, { useState } from 'react';
import { UserPlus, Edit, Trash2, UserCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext'; // Updated import path
import type { User, UserRole, TeamName } from '../types/core';

const UserManagement: React.FC = () => {
  const { currentUser, getAllUsers, updateUser, deleteUser, registerUser, adminSetUserPassword } = useAuth();
  const { t } = useLanguage();
  const [showAddUserModal, setShowAddUserModal] = useState(false); // Renamed for clarity
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const users = getAllUsers();
  const teams: TeamName[] = ['Marketing A', 'Marketing B', 'Marketing C', 'Marketing D', 'Marketing E', 'Production SBK', 'Production SDC'];

  const AddUserModal: React.FC = () => {
    const [formData, setFormData] = useState({
      email: editingUser?.email || '', // Pre-fill email if editing, though it's disabled
      username: editingUser?.username || '',
      password: '',
      fullName: editingUser?.fullName || '',
      role: (editingUser?.role || 'user') as UserRole,
      team: (editingUser?.team || 'Marketing A') as TeamName,
    });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { getAllUsers: fetchAllUsersForValidation } = useAuth(); // Alias for clarity

    const handleSubmit = async () => {
      setLoading(true);
      setError('');
      if (!formData.username.trim() || !formData.fullName.trim()) {
        setError(t('user.error.required'));
        setLoading(false);
        return;
      }

      if (!editingUser && (!formData.email.trim() || !formData.password.trim())) {
        setError(t('user.error.email.required') + " & " + t('user.error.password.required'));
        setLoading(false);
        return;
      }
      if (!editingUser && formData.password.trim() && formData.password.trim().length < 6) {
        setError(t('password.min.length'));
        setLoading(false);
        return;
      }
      if (editingUser && formData.password.trim() && formData.password.trim().length < 6) {
        setError(t('password.min.length'));
        setLoading(false);
        return;
      }

      const allCurrentUsers = fetchAllUsersForValidation();
      const existingUserByUsername = allCurrentUsers.find(u => u.username.toLowerCase() === formData.username.toLowerCase() && u.id !== editingUser?.id);
      if (existingUserByUsername) {
        setError(t('user.error.username.exists'));
        setLoading(false);
        return;
      }

      if (formData.role === 'supervisor') {
        const teamHasSupervisor = allCurrentUsers.some(user => user.team === formData.team && user.role === 'supervisor' && user.id !== editingUser?.id);
        if (teamHasSupervisor) {
          setError(t('user.error.supervisorExists', { teamName: formData.team }));
          setLoading(false);
          return;
        }
      }

      try {
        if (editingUser) {
          const updateData: Partial<User> & { password?: string } = {
            username: formData.username.trim(),
            fullName: formData.fullName.trim(),
            role: formData.role,
            team: formData.team,
          };
          // If admin supplied a new password, set it via Cloud Function first
          if (formData.password.trim()) {
            const resp = await adminSetUserPassword(editingUser.id, formData.password.trim());
            if (!resp.success) {
              setError(resp.error || t('user.error.update.failed', { defaultValue: 'Failed to update password.' }));
              setLoading(false);
              return;
            }
          }
          const success = await updateUser(editingUser.id, updateData);
          if (success) {
            setShowAddUserModal(false);
            setEditingUser(null);
          } else {
            setError(t('user.error.update.failed', { defaultValue: 'Failed to update user. Please try again.' }));
          }
        } else {
          const result = await registerUser(formData.email.trim(), formData.password, formData.username.trim(), formData.fullName.trim(), formData.role, formData.team);
          if (result.success) {
            setShowAddUserModal(false);
            setEditingUser(null);
            // Show success message - admin will need to sign back in
            alert(t('user.create.success.admin', { 
              defaultValue: 'User created successfully! You have been logged out for security reasons. Please sign in again to continue.' 
            }));
          } else {
            setError(result.error || t('error.registrationFailed', { defaultValue: 'Failed to create user' }));
          }
        }
      } catch (error: any) {
        let errorMessage = 'An unexpected error occurred';
        if (error.code === 'auth/network-request-failed') {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.code === 'permission-denied') {
          errorMessage = 'Permission denied. You may not have the required privileges.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        setError(errorMessage);
      }
      setLoading(false);
    };

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <h2 className="text-xl font-bold mb-4">{editingUser ? t('user.edit') : t('user.add')}</h2>
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-4">
            {!editingUser && (
              <div>
                <label htmlFor="user-email" className="block text-sm font-medium mb-1">Email Address *</label>
                <input 
                  id="user-email"
                  type="email" 
                  value={formData.email} 
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))} 
                  placeholder="user@company.com" 
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            )}
            <div>
              <label htmlFor="user-username" className="block text-sm font-medium mb-1">{t('auth.username')} *</label>
              <input 
                id="user-username"
                type="text" 
                value={formData.username} 
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))} 
                disabled={!!editingUser || loading} 
                placeholder="Enter username"
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="user-password" className="block text-sm font-medium mb-1">{t('auth.password')} {editingUser ? t('user.password.keep.blank') : '*'}</label>
              <input 
                id="user-password"
                type="password" 
                value={formData.password} 
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))} 
                disabled={loading} 
                placeholder={editingUser ? "Leave blank to keep current password" : "Enter password (min 6 characters)"}
                autoComplete={editingUser ? "new-password" : "new-password"}
              />
            </div>
            <div>
              <label htmlFor="user-fullname" className="block text-sm font-medium mb-1">{t('user.fullname')} *</label>
              <input 
                id="user-fullname"
                type="text" 
                value={formData.fullName} 
                onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))} 
                disabled={loading} 
                placeholder="Full name"
                autoComplete="name"
              />
            </div>
            {editingUser && editingUser.role === 'admin' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('user.role')}</label>
                <p className="p-2 border rounded bg-gray-100 text-gray-700">{t('user.admin')}</p>
              </div>
            ) : (
              <div>
                <label htmlFor="user-role" className="block text-sm font-medium text-gray-700 mb-1">{t('user.role')} *</label>
                <select 
                  id="user-role"
                  value={formData.role} 
                  onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value as UserRole }))} 
                  disabled={loading} 
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="user">{t('user.user')}</option>
                  <option value="supervisor">{t('user.supervisor')}</option>
                </select>
              </div>
            )}
            <div>
              <label htmlFor="user-team" className="block text-sm font-medium mb-1">{t('user.team')} *</label>
              <select 
                id="user-team"
                value={formData.team} 
                onChange={(e) => setFormData(prev => ({ ...prev, team: e.target.value as TeamName }))} 
                disabled={loading} 
                className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
              >
                {teams.map(team => (<option key={team} value={team}>{team}</option>))}
              </select>
            </div>
            {error && (<div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">{error}</div>)}
            <div className="flex justify-end space-x-2">
              <button type="button" onClick={() => { setShowAddUserModal(false); setEditingUser(null); }} className="btn-secondary px-4 py-2" disabled={loading}>{t('action.cancel')}</button>
              <button type="submit" disabled={loading} className="btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center">
                {loading && (<div className="animate-spin rounded-full w-4 h-4 border-2 border-white border-t-transparent mr-2"></div>)}
                {loading ? t('action.saving', { defaultValue: 'Saving...' }) : t('action.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{t('user.management')}</h1>
        <button onClick={() => { setEditingUser(null); setShowAddUserModal(true); }} className="btn-primary flex items-center">
          <UserPlus className="w-4 h-4 mr-2" />{t('user.add')}
        </button>
      </div>
      <div className="user-management-table-container bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t('auth.username')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Email</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t('user.fullname')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t('user.role')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t('user.team')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Created</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">{t('user.actions')}</th>
            </tr>
          </thead>
          <tbody className="table-body-border-top">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-gray-50 table-row-border-bottom">
                <td className="px-4 py-3 font-medium">{user.username}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                <td className="px-4 py-3">{user.fullName}</td>
                <td className="px-4 py-3">
                  <span className={`status-badge ${user.role === 'admin' ? 'bg-red-100 text-red-800' : user.role === 'supervisor' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                    {t(`user.${user.role}`)}
                  </span>
                </td>
                <td className="px-4 py-3">{user.team}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{user.createdAt && user.createdAt !== '' ? new Date(user.createdAt).toLocaleDateString() : 'No date'}</td>
                <td className="px-4 py-3">
                  <div className="flex space-x-2">
                    <button onClick={() => { setEditingUser(user); setShowAddUserModal(true); }} className="p-1 text-blue-600 hover:text-blue-800" title="Edit User">
                      <Edit className="w-4 h-4" />
                    </button>
                    {user.id !== currentUser?.id && (
                      <button 
                        onClick={async () => { 
                          if (window.confirm(t('user.delete.confirm', {username: user.username }))) { 
                            const success = await deleteUser(user.id); 
                            if (!success) alert('Failed to delete user. Please try again.'); 
                          }
                        }} 
                        className="p-1 text-red-600 hover:text-red-800" 
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  <UserCheck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No users found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {showAddUserModal && <AddUserModal />}
    </div>
  );
};

export default UserManagement;
